/**
 * Minimal Workflow Provider
 *
 * URL is the single source of truth. This provider just:
 * 1. Creates the store (which initializes from URL)
 * 2. Handles URL changes (popstate, Inertia navigation)
 * 3. Syncs substep to chat registry (so chat knows current stage)
 *
 * Note: We use Inertia's router.push() for client-side navigation (useBrainstormChat),
 * which properly registers with Inertia's history tracking. This eliminates the need
 * for custom history patching or URL detection hacks.
 */
import { createContext, useContext, useState, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { useStore, type StoreApi } from "zustand";
import { usePage } from "@inertiajs/react";
import { createWorkflowStore, type WorkflowStore, type LaunchFallback } from "@stores/workflowStore";
import { chatsRegistryStore } from "@stores/chatsRegistry";

type WorkflowStoreApi = StoreApi<WorkflowStore>;

const WorkflowContext = createContext<WorkflowStoreApi | null>(null);

interface WorkflowProviderProps {
  children: ReactNode;
}

export function WorkflowProvider({ children }: WorkflowProviderProps) {
  const { url, component, props } = usePage();

  // Thread URLs (/threads/:id) carry no step, so the workflow store can't parse
  // the launch position from the path. The page tells us its position via its
  // Inertia component name + project prop; pass that as a fallback the store
  // uses only when the URL doesn't parse.
  const projectUUID = (props as { project?: { uuid?: string | null } }).project?.uuid ?? null;
  const fallback: LaunchFallback = { component, projectUUID };
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const [store] = useState(() => createWorkflowStore(fallback));

  // Sync store from URL before paint. Mutating the store during render
  // synchronously notifies every OTHER subscribed component mid-render —
  // React's "Cannot update PaginationFooterProvider while rendering
  // WorkflowProvider" error. (The render-phase-state blessing covers a
  // component's OWN state, not an external store with subscribers.)
  // useLayoutEffect still runs before paint, so subscribers re-render
  // with fresh state without any visible staleness.
  useLayoutEffect(() => {
    store.getState().syncFromUrl(fallbackRef.current);
  }, [store, url]);

  // Sync from URL on browser back/forward
  useEffect(() => {
    const handlePopstate = () => {
      store.getState().syncFromUrl(fallbackRef.current);
    };

    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [store]);

  // Sync substep to chat registry so chats know current stage
  useEffect(() => {
    const unsubscribe = store.subscribe(
      (state) => ({ substep: state.substep, page: state.page }),
      ({ substep, page }) => {
        if (page && substep) {
          chatsRegistryStore.getState().syncStageToChat(page, substep);
        }
      },
      { fireImmediately: true }
    );
    return unsubscribe;
  }, [store]);

  return <WorkflowContext.Provider value={store}>{children}</WorkflowContext.Provider>;
}

/**
 * Hook to access workflow store with a selector
 */
export function useWorkflow<T>(selector: (state: WorkflowStore) => T): T {
  const store = useContext(WorkflowContext);
  if (!store) {
    throw new Error("useWorkflow must be used within WorkflowProvider");
  }
  return useStore(store, selector);
}

/**
 * Hook to access workflow store with optional selector (returns undefined if no provider)
 * Use this in components that might be rendered outside the provider
 */
export function useWorkflowOptional<T>(selector: (state: WorkflowStore) => T): T | undefined {
  const store = useContext(WorkflowContext);
  if (!store) {
    return undefined;
  }
  return useStore(store, selector);
}

// Re-export selectors for convenience
export {
  selectCanGoBack,
  selectCanGoForward,
  selectPageNumber,
  selectPage,
  selectSubstep,
  selectProjectUUID,
  selectCampaignUUID,
  selectNavigate,
  selectContinue,
  selectBack,
  selectPages,
  selectStep,
  selectHasVisitedReview,
  selectReturnToSection,
  selectReturnToReview,
  selectSetReturnToSection,
  selectClearReturnToSection,
  selectSetSubstep,
  WORKFLOW_STEPS,
} from "@stores/workflowStore";

export type { WorkflowStore };
